import type { ReconcileCandidate } from 'pages/documents/structuring/reconcile/types';
import type { ConsolidationAction } from './consolidation-utils';

// Every reconcile launch shows this when its scored candidates fail to load.
export const CANDIDATES_NOT_ASSEMBLED_MESSAGE =
  'Reconciliation candidates could not be assembled. Try again.';

/** Each selected row's fingerprint, by entity id. */
export type Fingerprints = Record<number, string>;
/** The tearsheet's candidate rows, by entity id. */
export type Rows = Record<number, ReconcileCandidate>;

/**
 * A 409 names the selection's live rows with their current fingerprints: a
 * selected row it no longer lists was merged or removed, and a listed one whose
 * fingerprint moved has changed.
 * @param {number[]} selectedIds - The selected rows' entity ids
 * @param {Fingerprints} current - The live rows' fingerprints from the 409
 * @param {Fingerprints | undefined} loaded - The fingerprints the selection was previewed with
 * @return {{ retired: number[], changed: number[] }} The retired and changed ids
 */
export const classifyStaleSelection = (
  selectedIds: number[],
  current: Fingerprints,
  loaded: Fingerprints | undefined
) => ({
  retired: selectedIds.filter((id) => !(id in current)),
  changed: selectedIds.filter(
    (id) => id in current && current[id] !== loaded?.[id]
  ),
});

/**
 * The error that tells the user which selected entities changed or went away.
 * @param {string[]} changed - Labels of the rows that changed
 * @param {string[]} retired - Labels of the rows merged or removed
 * @return {string} The message
 */
export const staleSelectionMessage = (changed: string[], retired: string[]) => {
  const parts = [
    changed.length > 0 && `${changed.join(', ')} changed`,
    retired.length > 0 &&
      `${retired.join(', ')} ${
        retired.length === 1 ? 'was' : 'were'
      } merged or removed`,
  ].filter(Boolean);
  return parts.length > 0
    ? `${parts.join(
        ' and '
      )} since you opened this. The selection was refreshed; review it and reconcile again.`
    : 'A selected entity changed since you opened this. The selection was refreshed; review it and reconcile again.';
};

/**
 * Reloaded data for the rows already listed. A row keeps its place in this
 * session (anchor pin, source label, Recommended badge, hard block); rows the
 * reload does not carry (the anchor, user-added rows) stay as they were.
 * @param {Rows} rows - The rows on screen
 * @param {ReconcileCandidate[]} fresh - The reloaded candidates
 * @return {Rows} The rows with the reloaded data applied
 */
export const refreshRows = (rows: Rows, fresh: ReconcileCandidate[]): Rows => {
  const next = { ...rows };
  fresh.forEach((c) => {
    const row = rows[c.id];
    if (!row) return;
    next[c.id] = {
      ...c,
      isStaged: row.isStaged,
      sourceLabel: row.sourceLabel,
      recommended: row.recommended,
      flagged: row.flagged,
    };
  });
  return next;
};

/**
 * The model actions that carry a reload into the consolidation model: the
 * address-flag changes (the submit gate) and renames (the name rows).
 * @param {Rows} rows - The rows on screen
 * @param {ReconcileCandidate[]} fresh - The reloaded candidates
 * @return {ConsolidationAction[]} The actions to dispatch, in order
 */
export const reloadActions = (
  rows: Rows,
  fresh: ReconcileCandidate[]
): ConsolidationAction[] => {
  const flagged: number[] = [];
  const cleared: number[] = [];
  const names: Record<number, string> = {};
  fresh.forEach((c) => {
    const row = rows[c.id];
    if (!row) return;
    if (c.addressFlagged && !row.addressFlagged) flagged.push(c.id);
    if (!c.addressFlagged && row.addressFlagged) cleared.push(c.id);
    if (c.name !== row.name) names[c.id] = c.name;
  });
  return [
    ...(flagged.length > 0
      ? [{ type: 'flagAddresses' as const, ids: flagged }]
      : []),
    ...cleared.map((id) => ({ type: 'clearAddressFlag' as const, id })),
    ...(Object.keys(names).length > 0
      ? [{ type: 'renameCandidates' as const, names }]
      : []),
  ];
};
